import React, { useEffect, useMemo, useState, useCallback } from "react";
import ReactQuill from '../../components/Common/ReactQuill';
import { Typeahead } from "react-bootstrap-typeahead";
import { toast } from "react-toastify";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";

/**
 * Helpers
 */
const filterObjectName = (collection, id, field) => {
  if (!Array.isArray(collection) || !id) return "";
  const item = collection.find((d) => d && String(d.id) === String(id));
  if (!item || !item[field]) return "";
  return item[field];
};

const lengthFromMinSec = (min, sec) =>
  Number(min || 0) * 60 + Number(sec || 0);

// Helper function to check if content is empty
const isContentEmpty = (content) => {
  if (!content) return true;
  // Remove HTML tags and check if there's actual content
  const strippedContent = content.replace(/<[^>]*>/g, '').trim();
  return strippedContent.length === 0;
};

const CreatePiece = (props) => {
  const {
    mode,
    isCreating,
    piecesInfo,
    accessMode,
    pieces_categories = [],
    initialValues = {},
    getAwsKey,
    onCreate,
    onEdit
  } = props;

  // ----- Local UI state -----
  const [description, setDescription] = useState(
    mode === "EDIT" && initialValues.description ? initialValues.description : ""
  );
  const [descriptionPost, setDescriptionPost] = useState(
    mode === "EDIT" && initialValues.description ? initialValues.description : ""
  );

  const [previewInputOn, setPreviewInputOn] = useState(false);
  const [completeScoreInputOn, setCompleteScoreInputOn] = useState(false);

  // Helper function to get initial selection
  const getInitialSelection = (itemOrId, collection, idField = 'id') => {
    if (!itemOrId) return [];

    // If it's already an object with an id, return it as array
    if (typeof itemOrId === 'object' && itemOrId.id) {
      return [itemOrId];
    }

    // If it's an ID, find the object in collection
    if (collection && Array.isArray(collection)) {
      const found = collection.find(item => item && String(item[idField]) === String(itemOrId));
      return found ? [found] : [];
    }

    return [];
  };

  // Typeahead controlled selections (arrays) - Fixed initialization
  const [composerSel, setComposerSel] = useState(() => {
    if (mode === "EDIT") {
      // Try both composer object and composer_id
      return getInitialSelection(
        initialValues.composer || initialValues.composer_id,
        piecesInfo?.composers
      );
    }
    return [];
  });

  const [eraSel, setEraSel] = useState(() => {
    if (mode === "EDIT") {
      // Try both era object and era_id
      return getInitialSelection(
        initialValues.era || initialValues.era_id,
        piecesInfo?.eras
      );
    }
    return [];
  });

  const [difficultySel, setDifficultySel] = useState(() => {
    if (mode === "EDIT") {
      // Try both difficulty object and difficulty_id
      return getInitialSelection(
        initialValues.difficulty || initialValues.difficulty_id,
        piecesInfo?.difficulties
      );
    }
    return [];
  });

  const [artistSel, setArtistSel] = useState(() => {
    if (mode === "EDIT") {
      // Try both artist object and artist_id
      return getInitialSelection(
        initialValues.artist || initialValues.artist_id,
        piecesInfo?.artists
      );
    }
    return [];
  });

  // S3 upload state
  const [awsKeysImage, setAwsKeysImage] = useState({});
  const [awsKeysPreview, setAwsKeysPreview] = useState({});
  const [awsKeysComplete, setAwsKeysComplete] = useState({});
  const [fileImage, setFileImage] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [fileComplete, setFileComplete] = useState(null);

  const [isUploading, setIsUploading] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // Update selections when piecesInfo changes (for EDIT mode)
  useEffect(() => {
    if (mode === "EDIT" && piecesInfo) {
      // Update composer selection
      if (!composerSel.length && (initialValues.composer || initialValues.composer_id)) {
        const newSel = getInitialSelection(
          initialValues.composer || initialValues.composer_id,
          piecesInfo.composers
        );
        if (newSel.length) setComposerSel(newSel);
      }

      // Update era selection
      if (!eraSel.length && (initialValues.era || initialValues.era_id)) {
        const newSel = getInitialSelection(
          initialValues.era || initialValues.era_id,
          piecesInfo.eras
        );
        if (newSel.length) setEraSel(newSel);
      }

      // Update difficulty selection
      if (!difficultySel.length && (initialValues.difficulty || initialValues.difficulty_id)) {
        const newSel = getInitialSelection(
          initialValues.difficulty || initialValues.difficulty_id,
          piecesInfo.difficulties
        );
        if (newSel.length) setDifficultySel(newSel);
      }

      // Update artist selection
      if (!artistSel.length && (initialValues.artist || initialValues.artist_id)) {
        const newSel = getInitialSelection(
          initialValues.artist || initialValues.artist_id,
          piecesInfo.artists
        );
        if (newSel.length) setArtistSel(newSel);
      }
    }
  }, [mode, piecesInfo, initialValues]);

  // ----- Derive initial min/sec from length -----
  const initialMinSec = useMemo(() => {
    if (mode !== "EDIT" || !initialValues?.length) return { lengthMin: 0, lengthSec: 0 };
    const len = Number(initialValues.length || 0);
    return { lengthMin: Math.floor(len / 60), lengthSec: len % 60 };
  }, [mode, initialValues?.length]);

  // ----- Formik initial values -----
  const formikInitial = useMemo(
    () => ({
      title: mode === "EDIT" ? initialValues.title || "" : "",
      pieceproductid_google: mode === "EDIT" ? (initialValues.pieceproductid_google || "") : "",
      price: mode === "EDIT" ? (initialValues.price ?? "") : "",
      category: mode === "EDIT" ? (initialValues.category || "") : "",
      preview_image: mode === "EDIT" ? (initialValues.preview_image || "") : "",
      preview: mode === "EDIT" ? (initialValues.preview || "") : "",
      complete_score: mode === "EDIT" ? (initialValues.complete_score || "") : "",
      lengthMin: initialMinSec.lengthMin,
      lengthSec: initialMinSec.lengthSec
    }),
    [mode, initialValues, initialMinSec]
  );

  // ----- Validation -----
  const Schema = Yup.object().shape({
    title: Yup.string().trim().required("Required"),
    price: Yup.number()
      .typeError("Must be a number")
      .min(0, "Must be >= 0")
      .required("Required"),
    lengthMin: Yup.number().min(0, ">= 0").required("Required"),
    lengthSec: Yup.number().min(0).max(59, "< 60").required("Required"),
  });

  // ----- Rich text - Updated for ReactQuill with null check -----
  const onDescriptionChange = (value) => {
    // ReactQuill can sometimes pass null
    const safeValue = value || '';
    setDescription(safeValue);
    setDescriptionPost(safeValue);
  };

  // Quill toolbar configuration with safer options
  const modules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      [{ 'indent': '-1' }, { 'indent': '+1' }],
      ['link', 'blockquote'],
      ['clean']
    ],
    clipboard: {
      // Prevent XSS attacks
      matchVisual: false
    }
  };

  const formats = [
    'header',
    'bold', 'italic', 'underline', 'strike', 'blockquote',
    'list', 'bullet', 'indent',
    'link'
  ];

  // ----- S3 helpers -----
  const buildKeyFolder = (type) => {
    switch (type) {
      case "preview":
        return "Preview/images";
      case "complete_score":
        return "Complete_Score/media";
      default:
        return "images";
    }
  };

  const requestAwsKey = async (type, file) => {
    try {
      const safeName = file.name.replace(/ /g, "_");
      const folder = buildKeyFolder(type);
      const aws = await getAwsKey({ key: `pieces/${folder}/${safeName}` });
      return aws;
    } catch (error) {
      console.error('Error getting AWS key:', error);
      throw new Error('Failed to get upload credentials');
    }
  };

  const uploadToS3 = async (awsKeys, file) => {
    if (!awsKeys || !file) return;

    try {
      const fd = new FormData();
      fd.append("key", awsKeys.key || "NA");
      fd.append("acl", awsKeys.acl || "NA");
      fd.append("X-Amz-Credential", awsKeys["x-amz-credential"] || "NA");
      fd.append("X-Amz-Algorithm", awsKeys["x-amz-algorithm"] || "NA");
      fd.append("X-Amz-Date", awsKeys["x-amz-date"] || "NA");
      fd.append("Policy", awsKeys.policy || "NA");
      fd.append("X-Amz-Signature", awsKeys["x-amz-signature"] || "NA");
      fd.append("file", file);

      const response = await fetch(awsKeys.action, { method: "POST", body: fd });
      if (!response.ok) {
        throw new Error('Upload failed');
      }

      return `${awsKeys.action}${awsKeys.key}`;
    } catch (error) {
      console.error('Error uploading to S3:', error);
      throw new Error('Failed to upload file');
    }
  };

  // ----- File change handlers -----
  const onFileChange = useCallback(
    (type, setFormikValue) => async (e) => {
      const f = e.target.files && e.target.files[0];
      if (!f) return;

      try {
        const keys = await requestAwsKey(type, f);

        if (type === "preview") {
          setAwsKeysPreview(keys);
          setFilePreview(f);
          setFormikValue("preview", `${keys.action}${keys.key}`);
        } else if (type === "complete_score") {
          setAwsKeysComplete(keys);
          setFileComplete(f);
          setFormikValue("complete_score", `${keys.action}${keys.key}`);
        } else {
          setAwsKeysImage(keys);
          setFileImage(f);
          setFormikValue("preview_image", `${keys.action}${keys.key}`);
        }

      } catch (err) {
        console.error(err);
        toast.error(err.message || "Failed to prepare upload.");
      }
    },
    [getAwsKey]
  );

  // Helper function to safely get ID from either object or ID value
  const getIdFromValue = (value) => {
    if (!value) return null;
    if (typeof value === 'object' && value.id) return value.id;
    return value;
  };

  // ----- Submit with better error handling -----
  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      setSubmitting(true);
      setIsUploading(true);
      setSubmitError(null);

      // Upload any pending files first
      let uploadedPreviewImage = values.preview_image;
      let uploadedPreview = values.preview;
      let uploadedComplete = values.complete_score;

      if (fileImage && awsKeysImage.key) {
        uploadedPreviewImage = await uploadToS3(awsKeysImage, fileImage);
        toast.success("Piece image uploaded.");
      }

      if (filePreview && awsKeysPreview.key && previewInputOn) {
        uploadedPreview = await uploadToS3(awsKeysPreview, filePreview);
      }

      if (fileComplete && awsKeysComplete.key && completeScoreInputOn) {
        uploadedComplete = await uploadToS3(awsKeysComplete, fileComplete);
      }

      // Build payload - get IDs from selections
      const composer_id = composerSel?.[0]?.id;
      const era_id = eraSel?.[0]?.id;
      const difficulty_id = difficultySel?.[0]?.id;
      const artist_id = artistSel?.[0]?.id;

      const len = lengthFromMinSec(values.lengthMin, values.lengthSec);

      const common = {
        title: values.title,
        composer_id,
        era_id,
        difficulty_id,
        description: descriptionPost,
        preview_image: uploadedPreviewImage,
        length: Number(len),
        artist_id,
        price: values.price,
        preview: uploadedPreview,
        category: values.category || "",
        complete_score: uploadedComplete,
        pieceproductid_google: values.pieceproductid_google || ""
      };

      // Guard validations with better empty content check
      if (isContentEmpty(descriptionPost)) {
        throw new Error("Description is required.");
      }
      if (!uploadedPreviewImage) {
        throw new Error("Preview Image is required.");
      }
      if (!composer_id || !artist_id || !era_id || !difficulty_id) {
        throw new Error("Composer/Artist/Era/Difficulty are required.");
      }
      if (len <= 0) {
        throw new Error("Video length must be > 0.");
      }

      if (mode === "EDIT") {
        // Get current names from selections
        const artistName = artist_id
          ? filterObjectName(piecesInfo.artists, artist_id, "title")
          : "";
        const composerName = composer_id
          ? filterObjectName(piecesInfo.composers, composer_id, "name")
          : "";
        const difficultyName = difficulty_id
          ? filterObjectName(piecesInfo.difficulties, difficulty_id, "name")
          : "";
        const eraName = era_id
          ? filterObjectName(piecesInfo.eras, era_id, "name")
          : "";

        // Get previous IDs (handling both object and ID formats)
        const artist_id_prev = getIdFromValue(initialValues.artist) ||
          getIdFromValue(initialValues.artist_id);
        const composer_id_prev = getIdFromValue(initialValues.composer) ||
          getIdFromValue(initialValues.composer_id);
        const difficulty_id_prev = getIdFromValue(initialValues.difficulty) ||
          getIdFromValue(initialValues.difficulty_id);
        const era_id_prev = getIdFromValue(initialValues.era) ||
          getIdFromValue(initialValues.era_id);

        // Get previous names
        const artistNamePrev = artist_id_prev
          ? filterObjectName(piecesInfo.artists, artist_id_prev, "title")
          : "";
        const composerNamePrev = composer_id_prev
          ? filterObjectName(piecesInfo.composers, composer_id_prev, "name")
          : "";
        const difficultyNamePrev = difficulty_id_prev
          ? filterObjectName(piecesInfo.difficulties, difficulty_id_prev, "name")
          : "";
        const eraNamePrev = era_id_prev
          ? filterObjectName(piecesInfo.eras, era_id_prev, "name")
          : "";

        // Wrap in Promise.resolve to handle both sync and async
        await Promise.resolve(onEdit(
          {
            ...common,
            id: initialValues.id,
            artistName,
            artistNamePrev,
            composerName,
            composerNamePrev,
            difficultyName,
            difficultyNamePrev,
            eraName,
            eraNamePrev
          },
          initialValues
        ));
      } else {
        const artistName = filterObjectName(piecesInfo.artists, artist_id, "title");
        const composerName = filterObjectName(piecesInfo.composers, composer_id, "name");
        const difficultyName = filterObjectName(piecesInfo.difficulties, difficulty_id, "name");
        const eraName = filterObjectName(piecesInfo.eras, era_id, "name");

        // Wrap in Promise.resolve to handle both sync and async
        await Promise.resolve(onCreate({
          ...common,
          artistName,
          composerName,
          difficultyName,
          eraName
        }));
      }

      toast.success(mode === "EDIT" ? "Piece updated." : "Piece created.");

      // Reset file states after successful submission
      setAwsKeysImage({});
      setAwsKeysPreview({});
      setAwsKeysComplete({});
      setFileImage(null);
      setFilePreview(null);
      setFileComplete(null);

    } catch (err) {
      console.error('Submit error:', err);

      // Handle specific error types
      if (err.response) {
        if (err.response.status === 401) {
          // Authentication error
          setSubmitError("Session expired. Please login again.");
          toast.error("Authentication error. Please login again.");
        } else if (err.response.status === 400) {
          // Validation error
          const message = err.response.data?.message || "Validation error";
          setSubmitError(message);
          toast.error(message);
        } else {
          // Other server errors
          const message = err.response.data?.message || "Server error occurred";
          setSubmitError(message);
          toast.error(message);
        }
      } else if (err.request) {
        // Network error
        setSubmitError("Network error. Please check your connection.");
        toast.error("Network error. Please check your connection.");
      } else {
        // Other errors
        setSubmitError(err.message || "Something went wrong.");
        toast.error(err.message || "Something went wrong.");
      }
    } finally {
      setIsUploading(false);
      setSubmitting(false);
    }
  };

  const isView = accessMode === "view";

  return (
    <section className="well">
      {/* Display any submit errors */}
      {submitError && (
        <div className="alert alert-danger" role="alert">
          {submitError}
          <button
            type="button"
            className="close"
            onClick={() => setSubmitError(null)}
            aria-label="Close"
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
      )}

      <Formik
        enableReinitialize
        initialValues={formikInitial}
        validationSchema={Schema}
        onSubmit={handleSubmit}
      >
        {({ values, errors, touched, setFieldValue, isSubmitting }) => (
          <Form>
            {/* Name */}
            <div className="row">
              <div className="col-md-3"><label>Name</label></div>
              <div className="col-md-9">
                <Field
                  name="title"
                  placeholder="Title"
                  disabled={isView}
                  className={`form-control ${isView ? "disabled" : ""}`}
                />
                {touched.title && errors.title && (
                  <div className="text-danger small">{errors.title}</div>
                )}
              </div>
            </div>

            {/* Google Product ID (Edit only) */}
            {mode === "EDIT" && (
              <div className="row">
                <div className="col-md-3"><label>Product ID for Google</label></div>
                <div className="col-md-9">
                  <Field
                    name="pieceproductid_google"
                    placeholder="Piece product ID for Google"
                    disabled={true}
                    className={`form-control ${isView ? "disabled" : ""}`}
                  />
                </div>
              </div>
            )}

            {/* Description */}
            <div className="row">
              <div className="col-md-3"><label>Description</label></div>
              <div className="col-md-9">
                <div className="form-group">
                  <ReactQuill
                    readOnly={isView}
                    value={description}
                    onChange={onDescriptionChange}
                    modules={modules}
                    formats={formats}
                    theme="snow"
                    placeholder="Enter piece description..."
                    style={{ minHeight: '200px' }}
                  />
                  {isSubmitting && isContentEmpty(descriptionPost) && (
                    <div className="text-danger small mt-1">Description is required</div>
                  )}
                </div>
              </div>
            </div>

            {/* Video Length */}
            <div className="row">
              <div className="col-md-3"><label>Video Length</label></div>
              <div className="col-md-9">
                <div className="col-md-4 form-group" style={{ paddingLeft: 0 }}>
                  <Field
                    name="lengthMin"
                    type="number"
                    min={0}
                    placeholder="Minutes"
                    disabled={isView}
                    className={`form-control ${isView ? "disabled" : ""}`}
                  />
                  {touched.lengthMin && errors.lengthMin && (
                    <div className="text-danger small">{errors.lengthMin}</div>
                  )}
                </div>
                <div className="col-md-2">Min</div>
                <div className="col-md-4 form-group" style={{ paddingLeft: 0 }}>
                  <Field
                    name="lengthSec"
                    type="number"
                    min={0}
                    max={59}
                    placeholder="Seconds"
                    disabled={isView}
                    className={`form-control ${isView ? "disabled" : ""}`}
                  />
                  {touched.lengthSec && errors.lengthSec && (
                    <div className="text-danger small">{errors.lengthSec}</div>
                  )}
                </div>
                <div className="col-md-2">Sec</div>
              </div>
            </div>

            {/* Price */}
            <div className="row">
              <div className="col-md-3"><label>Price</label></div>
              <div className="col-md-9">
                <Field
                  name="price"
                  type="number"
                  placeholder="Price"
                  disabled={isView}
                  className={`form-control ${isView ? "disabled" : ""}`}
                />
                {touched.price && errors.price && (
                  <div className="text-danger small">{errors.price}</div>
                )}
              </div>
            </div>

            {/* Complete Score: Link / Upload */}
            <div className="row">
              <div className="col-md-3"><label>Complete Score</label></div>
              <div className="col-md-9 radio-part">
                <div className="currentFile">{values.complete_score}</div>
                <div className="radio-selection row">
                  <div className="col-md-4">
                    <input
                      type="radio"
                      id="completeScoreInputType"
                      name="completeScoreType"
                      checked={!completeScoreInputOn}
                      onChange={() => setCompleteScoreInputOn(false)}
                      disabled={isView}
                    />
                    &nbsp;<label htmlFor="completeScoreInputType">Link</label>
                  </div>
                  <div className="col-md-4">
                    <input
                      type="radio"
                      id="completeScoreUploadType"
                      name="completeScoreType"
                      checked={completeScoreInputOn}
                      onChange={() => setCompleteScoreInputOn(true)}
                      disabled={isView}
                    />
                    &nbsp;<label htmlFor="completeScoreUploadType">Upload</label>
                  </div>
                </div>

                {completeScoreInputOn ? (
                  <input
                    id="uploadCompleteScoreImage"
                    type="file"
                    name="file"
                    disabled={isView}
                    onChange={onFileChange("complete_score", (n, v) =>
                      setFieldValue("complete_score", v)
                    )}
                  />
                ) : (
                  <Field
                    name="complete_score"
                    placeholder="Complete Score"
                    disabled={isView}
                    className={`form-control ${isView ? "disabled" : ""}`}
                  />
                )}
              </div>
            </div>

            {/* Category */}
            <div className="row category">
              <div className="col-md-3"><label>Category</label></div>
              <div className="col-md-9">
                <select
                  className="form-control"
                  disabled={isView}
                  value={values.category || ""}
                  onChange={(e) => setFieldValue("category", e.target.value)}
                >
                  <option value="">---Select Category---</option>
                  {pieces_categories.map((d, i) => (
                    <option key={i} value={d}>{d}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Composer + Era */}
            <div className="row">
              <div className="col-md-3"><label>Composer</label></div>
              <div className="col-md-9">
                <Typeahead
                  id="composer_ta"
                  labelKey="name"
                  options={piecesInfo.composers || []}
                  selected={composerSel}
                  onChange={setComposerSel}
                  disabled={isView}
                  placeholder="Composer"
                  className={`form-group ${isView ? "disabled" : ""}`}
                />
              </div>

              <div className="col-md-3"><label>Era</label></div>
              <div className="col-md-9">
                <Typeahead
                  id="era_ta"
                  labelKey="name"
                  options={piecesInfo.eras || []}
                  selected={eraSel}
                  onChange={setEraSel}
                  disabled={isView}
                  placeholder="Era"
                  className={`form-group ${isView ? "disabled" : ""}`}
                />
              </div>
            </div>

            {/* Difficulty */}
            <div className="row">
              <div className="col-md-3"><label>Difficulty</label></div>
              <div className="col-md-9">
                <Typeahead
                  id="difficulty_ta"
                  labelKey="name"
                  options={piecesInfo.difficulties || []}
                  selected={difficultySel}
                  onChange={setDifficultySel}
                  disabled={isView}
                  placeholder="Difficulty"
                  className={`form-group ${isView ? "disabled" : ""}`}
                />
              </div>
            </div>

            {/* Artist */}
            <div className="row">
              <div className="col-md-3"><label>Artist</label></div>
              <div className="col-md-9">
                <Typeahead
                  id="artist_ta"
                  labelKey="title"
                  options={piecesInfo.artists || []}
                  selected={artistSel}
                  onChange={setArtistSel}
                  disabled={isView}
                  placeholder="Artist"
                  className={`form-group ${isView ? "disabled" : ""}`}
                />
              </div>
            </div>

            {/* Preview: Link / Upload */}
            <div className="row">
              <div className="col-md-3"><label>Preview</label></div>
              <div className="col-md-9 radio-part">
                <div className="currentFile">{values.preview}</div>
                <div className="radio-selection row">
                  <div className="col-md-4">
                    <input
                      type="radio"
                      id="linkType"
                      name="previewType"
                      checked={!previewInputOn}
                      onChange={() => setPreviewInputOn(false)}
                      disabled={isView}
                    />
                    &nbsp;<label htmlFor="linkType">Link</label>
                  </div>
                  <div className="col-md-4">
                    <input
                      type="radio"
                      id="uploadType"
                      name="previewType"
                      checked={previewInputOn}
                      onChange={() => setPreviewInputOn(true)}
                      disabled={isView}
                    />
                    &nbsp;<label htmlFor="uploadType">Upload</label>
                  </div>
                </div>

                {previewInputOn ? (
                  <input
                    id="uploadPreviewImage"
                    type="file"
                    name="file"
                    disabled={isView}
                    onChange={onFileChange("preview", (n, v) =>
                      setFieldValue("preview", v)
                    )}
                  />
                ) : (
                  <Field
                    name="preview"
                    placeholder="Preview"
                    disabled={isView}
                    className={`form-control ${isView ? "disabled" : ""}`}
                  />
                )}
              </div>
            </div>

            {/* Current image (EDIT) */}
            {mode === "EDIT" && (values.preview_image ? (
              <div className="row">
                <div className="col-md-3"><label>Current Image</label></div>
                <div className="col-md-9">{values.preview_image}</div>
              </div>
            ) : null)}

            {/* Upload Piece Image (S3) + Submit */}
            {accessMode === "edit" && (
              <div className="row">
                <div className="col-md-3"><label>Upload Image</label></div>
                <div className="col-md-9">
                  <input
                    id="uploadPieceImage"
                    type="file"
                    name="file"
                    onChange={onFileChange("image", (n, v) =>
                      setFieldValue("preview_image", v)
                    )}
                  />
                  <button
                    type="submit"
                    className="cus-btn-primary btn-block btn-lg submitButton"
                    disabled={
                      isSubmitting ||
                      isUploading ||
                      !values.title ||
                      isContentEmpty(descriptionPost) ||
                      lengthFromMinSec(values.lengthMin, values.lengthSec) <= 0 ||
                      !values.price ||
                      !(values.preview_image) ||
                      composerSel.length === 0 ||
                      eraSel.length === 0 ||
                      difficultySel.length === 0 ||
                      artistSel.length === 0
                    }
                  >
                    {(isCreating || isUploading || isSubmitting) ? (
                      <span className="spinner" />
                    ) : (
                      <span>{mode === "EDIT" ? "Update" : "Create"}</span>
                    )}
                  </button>
                </div>
              </div>
            )}
          </Form>
        )}
      </Formik>
    </section>
  );
};

export default CreatePiece;
